import { Set as ISet, List } from '@gitbook/util-immutable';
import { assertNever } from '@gitbook/util-ts';

import { Block } from './block';
import { Data } from './data';
import { Document } from './document';
import { Fragment } from './fragment';
import { Inline } from './inline';
import { Leaf } from './leaf';
import { Mark } from './mark';
import { Operation, type OperationType } from './operation';
import { Text } from './text';
import { TextRange } from './text-range';
import { Value } from './value';

export type DataJSON = object;

/** JSON representation of a mark */
export interface MarkJSON {
    object: 'mark';
    data: DataJSON;
    type: string;
}

/** JSON representation of a leaf */
export interface LeafJSON {
    object: 'leaf';
    text: string;
    marks: MarkJSON[];
}

/** JSON representation of a Text. */
export interface TextJSON {
    key?: string;
    object: 'text';
    leaves: LeafJSON[];
}

/** JSON representation of an inline node. */
export interface InlineJSON {
    object: 'inline';
    key?: string;
    type: string;
    isVoid: boolean;
    data: DataJSON;
    nodes: Array<InlineJSON | TextJSON>;
    fragments?: FragmentJSON[];
}

/** JSON representation of a fragment */
export interface FragmentJSON {
    object: 'fragment';
    key?: string;
    fragment?: string;
    type?: string;
    nodes: Array<BlockJSON | InlineJSON>;
}

/** JSON representation of a block node */
export interface BlockJSON {
    object: 'block';
    key?: string;
    data: DataJSON;
    isVoid: boolean;
    nodes: Array<BlockJSON | InlineJSON>;
    fragments?: FragmentJSON[];
    type: string;
}

/** JSON representation of a document node */
export interface DocumentJSON {
    key?: string;
    object: 'document';
    data: DataJSON;
    nodes: BlockJSON[];
}

/** JSON representation of a range */
export interface RangeJSON {
    object: 'range';
    anchorKey: string | null;
    anchorOffset: number;
    focusKey: string | null;
    focusOffset: number;
    isBackward: boolean | null;
    isFocused: boolean;
    isAtomic: boolean;
    marks: MarkJSON[] | null;
    data: DataJSON;
}

/** JSON representation of a value node */
export interface ValueJSON {
    object: 'value';
    document: DocumentJSON;
    data?: DataJSON;
    selection?: Omit<RangeJSON, 'anchorKey' | 'focusKey'> & {
        anchorKey?: string | null;
        focusKey?: string | null;
        anchorPath?: number[];
        focusPath?: number[];
    };
}

/** JSON representation of an operation */
export interface OperationJSON {
    object: 'operation';
    type: OperationType;
    [key: string]: any;
}

export type NodeJSON =
    | MarkJSON
    | LeafJSON
    | TextJSON
    | InlineJSON
    | BlockJSON
    | FragmentJSON
    | DocumentJSON
    | ValueJSON
    | RangeJSON
    | OperationJSON;
export type NodeType =
    | Mark
    | Leaf
    | Text
    | Inline
    | Block
    | Fragment
    | Document
    | Value
    | TextRange
    | Operation;
export type KeyedNodeType = Text | Inline | Block | Fragment | Document;

/**
 * Create a `Node` from a JSON `value`
 */
export function deserialize(value: MarkJSON): Mark;
export function deserialize(value: LeafJSON): Leaf;
export function deserialize(value: TextJSON): Text;
export function deserialize(value: InlineJSON): Inline;
export function deserialize(value: BlockJSON): Block;
export function deserialize(value: FragmentJSON): Fragment;
export function deserialize(value: DocumentJSON): Document;
export function deserialize(value: ValueJSON): Value;
export function deserialize(value: OperationJSON): Operation;
export function deserialize(value: RangeJSON): TextRange;
export function deserialize(value: NodeJSON): NodeType {
    // Handle "kind" to easily deserialzie document from gitbook-new
    switch (value.object || value.kind) {
        case 'mark':
            return Mark.create({
                type: value.type,
                data: value.data,
            });
        case 'leaf':
            return Leaf.create({
                text: value.text,
                marks: (value.marks ?? []).map((mark) => deserialize(mark)),
            });
        case 'text':
            return Text.create({
                ...value,
                leaves: value.leaves
                    ? value.leaves.map((leaf) => deserialize(leaf))
                    : // Legacy support for https://github.com/GitbookIO/gitbook-new/blob/master/modules/edition/serialize.js#L56
                      value.ranges.map((leaf) => deserialize({ ...leaf, kind: 'leaf' })),
            });
        case 'inline':
            return Inline.create({
                ...value,
                fragments: value.fragments
                    ? value.fragments.map((fragment) => deserialize(fragment))
                    : [],
                nodes: (value.nodes ?? []).map((node) => deserialize(node)),
            });
        case 'fragment':
            return Fragment.create({
                ...value,
                nodes: value.nodes.map((node) => deserialize(node)),
            });
        case 'block':
            return Block.create({
                ...value,
                fragments: value.fragments
                    ? value.fragments.map((fragment) => deserialize(fragment))
                    : [],
                nodes: (value.nodes ?? []).map((node) => deserialize(node)),
            });
        case 'document':
            return Document.create({
                ...value,
                nodes: value.nodes.map((node) => deserialize(node)),
            });
        case 'range':
            return TextRange.create(value);
        case 'value':
            return Value.create({
                document: deserialize(value.document),
                data: Data.create(value.data || {}),
                selection: value.selection ? deserialize(value.selection) : undefined,
            });
        case 'operation':
            return deserializeOperation(value);
        default: {
            assertNever(value);
        }
    }
}

interface SerializeOptions {
    /** Preserve keys on every nodes */
    preserveKeys?: boolean;

    /** Preserve data when serializing value */
    preserveDataValue?: boolean;

    /** Preserve selection when serializing value */
    preserveSelection?: boolean;

    /** Transform a node to add annotation */
    transform?: (node: NodeType, json: NodeJSON) => NodeJSON;
}

/**
 * Convert a `Node` to a JSON `value`.
 */
export function serialize(
    value: List<NodeType> | ISet<NodeType>,
    options?: SerializeOptions
): NodeJSON[];
export function serialize(value: Mark): MarkJSON;
export function serialize(value: Leaf): LeafJSON;
export function serialize(value: Text): TextJSON;
export function serialize(value: Inline): InlineJSON;
export function serialize(value: Fragment, options?: SerializeOptions): FragmentJSON;
export function serialize(value: Block, options?: SerializeOptions): BlockJSON;
export function serialize(value: Document, options?: SerializeOptions): DocumentJSON;
export function serialize(value: Operation, options?: SerializeOptions): OperationJSON;
export function serialize(value: TextRange): RangeJSON;
export function serialize(value: Value, options?: SerializeOptions): ValueJSON;
export function serialize(
    value: NodeType | List<NodeType> | ISet<NodeType>,
    options?: SerializeOptions
): NodeJSON | NodeJSON[] {
    const { transform = defaultTransform } = options || {};

    if (List.isList(value) || ISet.isSet(value)) {
        return value.toArray().map((el) => serialize(el, options));
    }

    return transform(
        value,
        (() => {
            switch (value.object) {
                case 'mark':
                    return {
                        object: value.object,
                        type: value.type,
                        data: value.data.toObject(),
                    };
                case 'leaf':
                    return {
                        object: value.object,
                        text: value.text,
                        marks: value.marks
                            .toArray()
                            .sort((a, b) => a.type.localeCompare(b.type))
                            .map((m) => serialize(m)),
                    };
                case 'text':
                    return {
                        object: value.object,
                        leaves: value
                            .getLeaves()
                            .toArray()
                            .map((leaf) => serialize(leaf)),
                        ...(options?.preserveKeys && { key: value.key }),
                    };
                case 'inline':
                    return {
                        object: value.object,
                        type: value.type,
                        isVoid: value.isVoid,
                        data: value.data.toObject(),
                        nodes: value.nodes.toArray().map((child) => serialize(child, options)),
                        ...(options?.preserveKeys && { key: value.key }),
                        ...(value.fragments.size
                            ? {
                                  fragments: value.fragments
                                      .toArray()
                                      .map(([_key, fragment]) => serialize(fragment, options)),
                              }
                            : {}),
                    };
                case 'fragment':
                    return {
                        object: value.object,
                        nodes: value.nodes.toArray().map((child) => serialize(child, options)),
                        ...(options?.preserveKeys && { key: value.key }),
                        ...(value.fragment && { fragment: value.fragment }),
                        ...(value.type && { type: value.type }),
                    };
                case 'block':
                    return {
                        object: value.object,
                        type: value.type,
                        isVoid: value.isVoid,
                        data: value.data.toObject(),
                        nodes: value.nodes.toArray().map((child) => serialize(child, options)),
                        ...(options?.preserveKeys && { key: value.key }),
                        ...(value.fragments.size
                            ? {
                                  fragments: value.fragments
                                      .toArray()
                                      .map(([_key, fragment]) => serialize(fragment, options)),
                              }
                            : {}),
                    };
                case 'document':
                    return {
                        object: value.object,
                        data: value.data.toObject(),
                        nodes: value.nodes.toArray().map((child) => serialize(child, options)),
                        ...(options?.preserveKeys && { key: value.key }),
                    };
                case 'range':
                    return {
                        object: value.object,
                        anchorKey: value.anchorKey,
                        anchorOffset: value.anchorOffset,
                        focusKey: value.focusKey,
                        focusOffset: value.focusOffset,
                        isBackward: value.isBackward,
                        isFocused: value.isFocused,
                        isAtomic: value.isAtomic,
                        marks:
                            value.marks == null
                                ? null
                                : value.marks.toArray().map((m) => serialize(m)),
                        data: value.data.toObject(),
                    };
                case 'value':
                    return serializeValue(value, options);
                case 'operation':
                    return serializeOperation(value, options);
                default: {
                    assertNever(value);
                }
            }
        })() as NodeJSON
    );
}

function serializeValue(value: Value, options?: SerializeOptions): ValueJSON {
    const object: ValueJSON = {
        object: value.object,
        document: serialize(value.document, options),
        ...(options?.preserveDataValue && { data: value.data.toObject() }),
    };

    // When serializing valuew ithout keys, we store paths for the selection.
    if (options?.preserveSelection && !options?.preserveKeys) {
        const { document, selection } = value;

        object.selection = serialize(value.selection);

        object.selection.anchorPath = selection.isSet
            ? document.getPath(selection.anchorKey)
            : null;

        object.selection.focusPath = selection.isSet ? document.getPath(selection.focusKey) : null;

        delete object.selection.anchorKey;
        delete object.selection.focusKey;
    }

    return object;
}

function serializeOperation(operation: Operation, options?: SerializeOptions): OperationJSON {
    switch (operation.type) {
        // Texts
        case 'insert_text':
        case 'remove_text':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                offset: operation.offset,
                text: operation.text,
                marks: operation.marks.toArray().map((mark) => serialize(mark, options)),
            };
        // Marks
        case 'add_mark':
        case 'remove_mark':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                offset: operation.offset,
                length: operation.length,
                mark: serialize(operation.mark, options),
            };
        // Nodes
        case 'insert_node':
        case 'remove_node':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                node: serialize(operation.node, options),
            };
        case 'move_node':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                newPath: operation.newPath,
            };
        case 'merge_node':
        case 'split_node':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                position: operation.position,
                properties: operation.properties,
                target: operation.target,
            };
        case 'set_node':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                node: serialize(operation.node, options),
                properties: operation.properties,
            };
        // Fragments
        case 'insert_fragment':
        case 'set_fragment':
        case 'remove_fragment':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                ...(operation.fragmentPath ? { fragmentPath: operation.fragmentPath } : {}),
                fragmentIndex: operation.fragmentIndex,
                node: serialize(operation.node, options),
            };
        // Data
        case 'set_data':
        case 'remove_data':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                dataPath: operation.dataPath,
                data: operation.data,
            };
        case 'insert_array_data':
        case 'remove_array_data':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                dataPath: operation.dataPath,
                data: operation.data,
                dataIndex: operation.dataIndex,
            };
        // Selection
        case 'set_selection':
            return {
                object: 'operation',
                type: operation.type,
                selection: serialize(operation.selection),
                properties: operation.properties,
            };
        // Document
        case 'set_document':
            return {
                object: 'operation',
                type: operation.type,
                data: serialize(operation.data),
            };
        default:
            assertNever(operation);
    }
}

function deserializeOperation(operation: OperationJSON): Operation {
    switch (operation.type) {
        // Texts
        case 'insert_text':
        case 'remove_text':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                offset: operation.offset,
                text: operation.text,
                marks: Mark.createSet(operation.marks.map((mark) => deserialize(mark))),
            });
        // Marks
        case 'add_mark':
        case 'remove_mark':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                offset: operation.offset,
                length: operation.length,
                mark: deserialize(operation.mark),
            });
        // Nodes
        case 'insert_node':
        case 'remove_node':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                node: deserialize(operation.node),
            });
        case 'move_node':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                newPath: operation.newPath,
            });
        case 'merge_node':
        case 'split_node':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                position: operation.position,
                properties: operation.properties,
                target: operation.target,
            });
        case 'set_node':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                node: deserialize(operation.node),
                properties: operation.properties,
            });
        // Fragments
        case 'insert_fragment':
        case 'set_fragment':
        case 'remove_fragment':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                ...(operation.fragmentPath ? { fragmentPath: operation.fragmentPath } : {}),
                fragmentIndex: operation.fragmentIndex,
                node: deserialize(operation.node),
            });
        // Data
        case 'set_data':
        case 'remove_data':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                dataPath: operation.dataPath,
                data: operation.data,
            });
        case 'insert_array_data':
        case 'remove_array_data':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                dataPath: operation.dataPath,
                data: operation.data,
                dataIndex: operation.dataIndex,
            });
        // Selection
        case 'set_selection':
            return Operation.create({
                type: operation.type,
                selection: deserialize(operation.selection as RangeJSON),
                properties: operation.properties,
            });
        // Document
        case 'set_document':
            return Operation.create({
                type: operation.type,
                data: deserialize(operation.data as DocumentJSON),
            });
        default:
            assertNever(operation);
    }
}

/**
 * Serialize an array of operations.
 */
export function serializeOperations(operations: Operation[]): string {
    const serialized: OperationJSON[] = operations.map((operation) =>
        serialize(operation, { preserveKeys: true })
    );
    const stringified = JSON.stringify(serialized, null, 2);
    return stringified;
}

/**
 * Deserialize an array of operations.
 */
export function deserializeOperations(operations: string): Operation[] {
    const parsed: OperationJSON[] = JSON.parse(operations);
    const deserialized: Operation[] = parsed.map((operation) => deserialize(operation));
    return deserialized;
}

function defaultTransform(_node: NodeType, json: NodeJSON): NodeJSON {
    return json;
}
